import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { get, map } from 'lodash/fp';
import PropTypes from 'prop-types';
import { batch, useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import UpdatePositionRequestSchema from '@careerstart/wae-common/schema/positionTemplates/update/update.patch.req';
import GPS_STRATEGY_TYPES from '@careerstart/wae-common/src/main/constants/gps';
import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ROUTE_CONSTANTS } from '../../../../main/views/app/RouteBuilding/routeConstants';
import BackdropCircularProgress from '../../../components/BackdropCircularProgress';
import WaePaginatedDataGrid from '../../../components/DataGrid';
import DetailView from '../../../components/DetailView/DetailView2';
import { validateSchema } from '../../../components/Form/validations';
import ListHeader from '../../../components/ListHeader';
import selectUser from '../../../store/selectors/appSelector';
import {
  selectCertifications,
  selectIsDeleteProcessing,
  selectIsLoading,
  selectIsLoadingCertifications,
  selectIsLoadingCorporations,
  selectIsLoadingLocations,
  selectIsUpdateProcessing,
  selectLocations,
  selectPositionTemplateListRowData,
  selectTotalRowCount,
} from '../../../store/selectors/positionTemplateListSelectors';
import theme, { dataGridFiltersHeight, listHeaderHeight, topBarHeight } from '../../../theme';
import { decimalToPercentageString, percentageStringToDecimal } from '../../../utils';
import launchDarklyToggles, { DARKLY_TOGGLE_NAMES } from '../../../utils/launchDarklyToggles';
import { postPositionTemplate } from '../createPositionTemplateDrawer';
import CreatePositionTemplateDrawer from '../createPositionTemplateDrawer/CreatePositionTemplateDrawer';

import AdminAdditionalDetails from './detail/AdminAdditionalDetailsFormWrapper';
import DeleteDrawer from './detail/DeleteDrawer';
import additionalFilters from './additionalFilters';
import {
  AdminOverview,
  AdminShiftDetail,
  EmployerOverview,
  EmployerShiftDetail,
  LegalInfo,
  RecruiterOverview,
  RecruiterShiftDetail,
} from './detail';
import getPositionTemplateListColumnData from './positionTemplateListColumnData';
import {
  getCertificationsForPositionTemplateList,
  getCorporationsForPositionTemplateList,
  getLocationsForPositionTemplateList,
  getPositionTemplateList,
} from './reducer';
import { deletePositionTemplate, updatePositionTemplate } from '.';

const RootGrid = styled(Grid)(() => ({
  height: `calc(100vh - ${topBarHeight})`,
  padding: theme.spacing(2),
}));

const PositionTemplateList = ({ flags }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);
  const positionTemplateListRowData = useSelector(selectPositionTemplateListRowData);
  const isLoadingLocations = useSelector(selectIsLoadingLocations);
  const locations = useSelector(selectLocations);
  const isLoadingCertifications = useSelector(selectIsLoadingCertifications);
  const certifications = useSelector(selectCertifications);
  const isLoadingCorporations = useSelector(selectIsLoadingCorporations);
  const isUpdateProcessing = useSelector(selectIsUpdateProcessing);
  const isDeleteProcessing = useSelector(selectIsDeleteProcessing);
  const mediumScreen = useMediaQuery(muiTheme.breakpoints.up('md'));
  const [positionTemplateId, setPositionTemplateId] = useState(null);
  const totalRowCount = useSelector(selectTotalRowCount);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = React.useState(10);
  const [isDeleteDrawerOpen, setIsDeleteDrawerOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const apiPath = 'position-templates/read';

  const positionTemplateListColumnData = useMemo(
    () => getPositionTemplateListColumnData(user.role),
    [user]
  );

  const selectedPosition = useMemo(
    () => positionTemplateListRowData.find(({ id }) => id === positionTemplateId),
    [positionTemplateListRowData, positionTemplateId]
  );

  const selectedData = useMemo(
    () =>
      selectedPosition
        ? {
            ...selectedPosition,
            locations: selectedPosition.locations.map((location) => ({
              ...location,
              name: `${location.description} - ${location.address}`,
            })),
            corporationName: get(['corporation', 'name'], selectedPosition),
            gpsStrategy: get(['gps', 'strategy'], selectedPosition),
            maxPunchDistance: get(['gps', 'maxDistance'], selectedPosition),
            overstaffingPercent: decimalToPercentageString(
              selectedPosition?.overstaffingPercent || 0
            ),
          }
        : null,
    [selectedPosition]
  );

  const allCorporationLocations = useMemo(() => {
    if (!selectedData) return [];
    const { corporation } = selectedData;

    return locations.filter(
      (location) => location.corporation && location.corporation.id === corporation._id
    );
  }, [locations, selectedData]);

  const positionTemplateListPaginationQuery = React.useCallback(
    (params) => dispatch(getPositionTemplateList(params)),
    [dispatch]
  );

  const isNewPositionTemplateDesignEnabled = launchDarklyToggles(
    flags,
    DARKLY_TOGGLE_NAMES.hb5547NewPtDesign
  );

  const onSelectionModelChange = (selectedPositionTemplateObject) => {
    const selectedId = selectedPositionTemplateObject?.id;
    if (selectedId && isNewPositionTemplateDesignEnabled) {
      navigate(generatePath(`/${ROUTE_CONSTANTS.POSITION_TEMPLATE_DETAIL}`, { id: selectedId }));
      return;
    }
    setPositionTemplateId(
      (selectedPositionTemplateObject && selectedPositionTemplateObject.id) || null
    );
  };

  const handleClose = () => {
    setPositionTemplateId(null);
  };

  const handlePositionTemplateUpdate = useCallback(
    (formData, initialValues) => {
      const currencyHelper = (fieldName) => {
        const rawValue = formData[fieldName];
        if (rawValue === undefined) return undefined;

        return (
          (rawValue &&
            typeof rawValue === 'string' &&
            Number(rawValue.replace(/[^0-9.-]+/g, ''))) ||
          rawValue
        );
      };

      const formatStringAsInteger = (value) => {
        if (value === undefined || value === null) return undefined;
        const number = Number(value);
        return Number.isNaN(number) ? undefined : number;
      };

      const getDropdownValue = (fieldName) => {
        const formValue = get(fieldName, formData);
        if (formValue?.value !== undefined) {
          return formValue.value;
        }
        // If no form value, the field hasn't been changed, so return undefined
        return undefined;
      };

      // Helper function to check if a field has changed from its initial value
      const hasFieldChanged = (fieldName) =>
        formData[fieldName] !== undefined && formData[fieldName] !== initialValues?.[fieldName];

      // Helper function for dropdown fields (compares the actual values, not the objects)
      const hasDropdownFieldChanged = (fieldName) => {
        const formValue = formData[fieldName];
        const initialValue = initialValues?.[fieldName];

        // If formValue is undefined, the field hasn't been touched
        if (formValue === undefined) {
          return false;
        }

        // Extract the actual values for comparison
        const formActualValue = formValue?.value !== undefined ? formValue.value : formValue;
        const initialActualValue =
          initialValue?.value !== undefined ? initialValue.value : initialValue;

        // Compare the actual values
        return formActualValue !== initialActualValue;
      };

      // Build fields object with only changed values
      const fields = {};

      // Only add fields that have actually changed
      if (hasFieldChanged('name')) {
        fields.name = formData.name;
      }
      if (hasFieldChanged('description')) {
        fields.description = formData.description;
      }
      if (hasFieldChanged('approved')) {
        fields.approved = formData.approved;
      }
      if (hasDropdownFieldChanged('benefitsPack')) {
        fields.benefitsPack =
          formData.benefitsPack?.value !== undefined
            ? formData.benefitsPack.value
            : formData.benefitsPack;
      }
      if (hasFieldChanged('billRateDT')) {
        fields.billRateDT = currencyHelper('billRateDT');
      }
      if (hasFieldChanged('billRateOT')) {
        fields.billRateOT = currencyHelper('billRateOT');
      }
      if (hasFieldChanged('billRateReg')) {
        fields.billRateReg = currencyHelper('billRateReg');
      }
      if (hasDropdownFieldChanged('branch')) {
        fields.branch =
          formData.branch?.value !== undefined ? formData.branch.value : formData.branch;
      }
      if (hasFieldChanged('certifications')) {
        fields.certifications = map((cert) => cert._id, formData.certifications);
      }
      if (hasFieldChanged('departments')) {
        fields.departments = map((dept) => dept._id, formData.departments);
      }
      if (hasFieldChanged('skills')) {
        fields.skills = map((skill) => skill._id, formData.skills);
      }
      if (hasDropdownFieldChanged('legalBusEntry')) {
        fields.legalBusEntry =
          formData.legalBusEntry?.value !== undefined
            ? formData.legalBusEntry.value
            : formData.legalBusEntry;
      }
      if (hasFieldChanged('locations')) {
        fields.locations = map((loc) => loc._id, formData.locations);
      }
      if (hasDropdownFieldChanged('payGroup')) {
        fields.payGroup =
          formData.payGroup?.value !== undefined ? formData.payGroup.value : formData.payGroup;
      }
      if (hasFieldChanged('overstaffingPercent')) {
        fields.overstaffingPercent = percentageStringToDecimal(formData.overstaffingPercent);
      }
      if (hasFieldChanged('payRateDT')) {
        fields.payRateDT = currencyHelper('payRateDT');
      }
      if (hasFieldChanged('payRateOT')) {
        fields.payRateOT = currencyHelper('payRateOT');
      }
      if (hasFieldChanged('payRateReg')) {
        fields.payRateReg = currencyHelper('payRateReg');
      }
      if (hasFieldChanged('saleRep')) {
        fields.saleRep = formData.saleRep;
      }
      if (hasFieldChanged('shifts')) {
        fields.shifts = formData.shifts;
      }
      if (hasFieldChanged('workersCompCode')) {
        const workersCompValue = formatStringAsInteger(formData.workersCompCode);
        if (workersCompValue !== undefined) {
          fields.workersCompCode = workersCompValue;
        }
      }
      if (hasFieldChanged('overtimeAllowed')) {
        fields.overtimeAllowed = formData.overtimeAllowed;
      }
      if (hasFieldChanged('allowBackfill')) {
        fields.allowBackfill = formData.allowBackfill;
      }
      if (hasFieldChanged('requireEmployerApproval')) {
        fields.requireEmployerApproval = formData.requireEmployerApproval;
      }

      // Handle GPS fields - only include if changed
      if (hasFieldChanged('gpsEnforced') || hasFieldChanged('punchDistance')) {
        const gpsFields = {};

        // Only set strategy if gpsEnforced has actually changed
        if (hasFieldChanged('gpsEnforced')) {
          gpsFields.strategy = formData.gpsEnforced
            ? GPS_STRATEGY_TYPES.ENFORCED
            : GPS_STRATEGY_TYPES.UNENFORCED;
        }

        if (hasFieldChanged('punchDistance')) {
          const distance = formatStringAsInteger(formData.punchDistance);
          if (distance !== undefined) {
            gpsFields.maxDistance = distance;
          }
        }

        if (Object.keys(gpsFields).length > 0) {
          fields.gps = gpsFields;
        }
      }

      // Handle timekeeping fields - only include if changed

      if (hasDropdownFieldChanged('earlyPunchWindow') || hasDropdownFieldChanged('punchRounding')) {
        const timekeepingFields = {};
        const punchesFields = {};

        if (hasDropdownFieldChanged('earlyPunchWindow')) {
          const earlyPunchValue = getDropdownValue('earlyPunchWindow');
          if (earlyPunchValue !== undefined) {
            punchesFields.window = {
              inEarly: earlyPunchValue,
            };
          }
        }

        if (hasDropdownFieldChanged('punchRounding')) {
          const punchRoundingValue = getDropdownValue('punchRounding');
          if (punchRoundingValue !== undefined) {
            punchesFields.rounding = {
              amount: punchRoundingValue,
            };
          }
        }

        if (Object.keys(punchesFields).length > 0) {
          timekeepingFields.punches = punchesFields;
          fields.timekeeping = timekeepingFields;
        }
      }

      // Handle flags - only include if changed

      if (
        hasFieldChanged('isEnforcingAttendancePolicy') ||
        hasFieldChanged('isPunchingOutsideOfShiftEnabled')
      ) {
        const flagsFields = {};

        if (hasFieldChanged('isEnforcingAttendancePolicy')) {
          flagsFields.isEnforcingAttendancePolicy = formData.isEnforcingAttendancePolicy;
        }
        if (hasFieldChanged('isPunchingOutsideOfShiftEnabled')) {
          flagsFields.isPunchingOutsideOfShiftEnabled = formData.isPunchingOutsideOfShiftEnabled;
        }

        if (Object.keys(flagsFields).length > 0) {
          fields.flags = flagsFields;
        }
      }

      const data = {
        id: positionTemplateId,
        fields,
      };

      if (get('FORM_ERROR', validateSchema(UpdatePositionRequestSchema, data))) {
        return;
      }

      dispatch(updatePositionTemplate(data));
    },
    [dispatch, positionTemplateId]
  );

  const handlePositionTemplateDelete = useCallback(() => {
    dispatch(deletePositionTemplate({ id: positionTemplateId }));
    handleClose();
  }, [dispatch, positionTemplateId]);

  const createPositionTemplateOnClick = () => {
    if (isNewPositionTemplateDesignEnabled) {
      navigate(`/${ROUTE_CONSTANTS.POSITION_TEMPLATE_CREATE}`);
      return;
    }
    setIsDrawerOpen(true);
  };

  useEffect(() => {
    setPositionTemplateId(null);
  }, [positionTemplateListRowData]);

  useEffect(() => {
    batch(() => {
      dispatch(getCertificationsForPositionTemplateList());
      dispatch(getCorporationsForPositionTemplateList());
      dispatch(getLocationsForPositionTemplateList());
    });
  }, [dispatch]);

  const tabData = (container) =>
    [
      {
        tabContent: (
          <Grid sx={{ width: '100%' }}>
            {(get('role', user) === 'admin' && (
              <AdminOverview
                availableLocations={allCorporationLocations} // TODO: Remove available locations once searchbar supports API filters!
                container={container}
                initialValues={selectedData}
                onDelete={setIsDeleteDrawerOpen}
                onUpdate={handlePositionTemplateUpdate}
              />
            )) ||
              (get('role', user) === 'recruiter' && (
                <RecruiterOverview
                  availableLocations={allCorporationLocations} // TODO: Remove available locations once searchbar supports API filters!
                  container={container}
                  initialValues={selectedData}
                  onUpdate={handlePositionTemplateUpdate}
                />
              )) ||
              (get('role', user) === 'employer' && (
                <EmployerOverview
                  availableLocations={allCorporationLocations} // TODO: Remove available locations once searchbar supports API filters!
                  container={container}
                  initialValues={selectedData}
                  onUpdate={handlePositionTemplateUpdate}
                />
              ))}
          </Grid>
        ),
        tabLabel: 'Overview',
      },
      {
        tabContent: (
          <Grid sx={{ width: '100%' }}>
            {(get('role', user) === 'admin' && (
              <AdminShiftDetail
                certifications={certifications}
                container={container}
                initialValues={selectedData}
                onUpdate={handlePositionTemplateUpdate}
              />
            )) ||
              (get('role', user) === 'recruiter' && (
                <RecruiterShiftDetail
                  certifications={certifications}
                  container={container}
                  initialValues={selectedData}
                  onUpdate={handlePositionTemplateUpdate}
                />
              )) ||
              (get('role', user) === 'employer' && (
                <EmployerShiftDetail
                  certifications={certifications}
                  container={container}
                  initialValues={selectedData}
                  onUpdate={handlePositionTemplateUpdate}
                />
              ))}
          </Grid>
        ),
        tabLabel: 'Shift Details',
      },
      user?.role === UserRole.ADMIN && {
        tabContent: (
          <Grid sx={{ width: '100%' }}>
            {get('role', user) === 'admin' && (
              <AdminAdditionalDetails
                requireEmployerApproval={selectedPosition?.requireEmployerApproval || false}
                allowBackfill={selectedPosition?.allowBackfill || false}
                overtimeAllowed={selectedPosition?.overtimeAllowed || false}
                gpsEnforced={selectedPosition?.gps?.strategy !== GPS_STRATEGY_TYPES.UNENFORCED}
                isEnforcingAttendancePolicy={
                  selectedPosition?.flags?.isEnforcingAttendancePolicy || false
                }
                isPunchingOutsideOfShiftEnabled={
                  selectedPosition?.flags?.isPunchingOutsideOfShiftEnabled || false
                }
                punchDistance={selectedPosition?.gps?.maxDistance}
                earlyPunchWindow={
                  selectedPosition?.timekeeping?.punches?.window?.inEarly !== undefined
                    ? {
                        id: selectedPosition.timekeeping.punches.window.inEarly.toString(),
                        name: LanguageConverterV2('pt.additionalDetails.minutes', {
                          minutes: selectedPosition.timekeeping.punches.window.inEarly,
                        }),
                        value: selectedPosition.timekeeping.punches.window.inEarly,
                      }
                    : {
                        id: '15',
                        name: LanguageConverterV2('pt.additionalDetails.minutes', { minutes: 15 }),
                        value: 15,
                      }
                }
                punchRounding={
                  selectedPosition?.timekeeping?.punches?.rounding?.amount !== undefined
                    ? {
                        id: selectedPosition.timekeeping.punches.rounding.amount.toString(),
                        name: LanguageConverterV2('pt.additionalDetails.minutes', {
                          minutes: selectedPosition.timekeeping.punches.rounding.amount,
                        }),
                        value: selectedPosition.timekeeping.punches.rounding.amount,
                      }
                    : {
                        id: '1',
                        name: LanguageConverterV2('pt.additionalDetails.minutes', { minutes: 1 }),
                        value: 1,
                      }
                }
                onUpdate={handlePositionTemplateUpdate}
              />
            )}
          </Grid>
        ),
        tabLabel: 'Additional Info',
      },
      user?.role === UserRole.ADMIN && {
        tabContent: (
          <Grid sx={{ width: '100%' }}>
            <LegalInfo
              positionTemplateId={positionTemplateId}
              paidBreakBehavior={selectedPosition?.timekeeping?.breaks?.paid?.behavior}
            />
          </Grid>
        ),
        tabLabel: 'Legal Info',
      },
    ].filter((tab) => !!tab);

  const Detail = () => (
    <DetailView
      close={handleClose}
      onUpdate={handlePositionTemplateUpdate}
      tabData={tabData}
      sx={{ marginLeft: theme.spacing(1.5), height: `calc(100% - ${listHeaderHeight})` }}
    />
  );

  return (
    <RootGrid container>
      <Slide
        direction="right"
        unmountOnExit
        in={!(!mediumScreen && positionTemplateId)}
        timeout={10}
        easing={{ enter: 'step-end', exit: 'step-start' }}
      >
        <Grid
          item
          container
          md
          xs
          sm
          sx={{
            height: `calc(100% - ${listHeaderHeight} - ${dataGridFiltersHeight} )`,
            marginRight: positionTemplateId && 2,
          }}
        >
          <ListHeader
            headerText="Positions"
            buttonText="New Position"
            handleOnClick={createPositionTemplateOnClick}
          />

          <Grid
            container
            sx={{
              height: `calc(100% - ${listHeaderHeight})`,
              width: '100%',
            }}
          >
            {positionTemplateListRowData && (
              <>
                <WaePaginatedDataGrid
                  apiPath={apiPath}
                  additiveFilters={additionalFilters}
                  columnData={positionTemplateListColumnData}
                  onSelectionModelChange={onSelectionModelChange}
                  loading={isLoading}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  pageSize={pageSize}
                  page={page}
                  paginatedData={positionTemplateListRowData}
                  pagination
                  paginationQuery={positionTemplateListPaginationQuery}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  setRowCount={setRowCount}
                  rowCount={rowCount}
                  totalRowCount={totalRowCount}
                  sx={{
                    border: 'none',
                    height: '100%',
                    width: '100%',
                  }}
                />
                {positionTemplateId && <Detail />}
              </>
            )}
          </Grid>
        </Grid>
      </Slide>
      <DeleteDrawer
        anchor="right"
        handlePositionTemplateDelete={() => handlePositionTemplateDelete()}
        isDeleteProcessing={isDeleteProcessing}
        isOpen={isDeleteDrawerOpen}
        onClose={() => setIsDeleteDrawerOpen(false)}
      />
      {!isLoadingCertifications && !isLoadingLocations && !isLoadingCorporations && (
        <>
          {!mediumScreen && positionTemplateId && <Detail />}

          {(isUpdateProcessing || isDeleteProcessing) && <BackdropCircularProgress />}
        </>
      )}
      <CreatePositionTemplateDrawer
        isOpen={isDrawerOpen}
        certifications={certifications}
        onClose={() => setIsDrawerOpen(false)}
        postPositionTemplate={(postPositionTemplateData) =>
          dispatch(postPositionTemplate(postPositionTemplateData))
        }
      />
    </RootGrid>
  );
};

PositionTemplateList.propTypes = {
  flags: PropTypes.shape({}),
};

export default withLDConsumer()(PositionTemplateList);
